import { makeAutoObservable, runInAction } from 'mobx';
import QuestionEntity, { EditableState, IQueryBody, IQuestion, TQuestionEntity } from './question-entetiy';
import * as queries from '../../graphql/queries';
import { QnadaReviewModeTabs, QuestionState, SenderStatus } from '../../constans/question-enums';
import * as subscriptions from '../../graphql/subscriptions';
import * as mutations from '../../graphql/mutations';
import {
  checkDuplicateOfPrioritizeIndexInList,
  getHigherPrioritizeByState,
  prioritizeQuestionsInGroup,
  reCalcEachItemPrioritizeIndex,
  sortByPrioritize,
  splitQuestionGroupChunks
} from '../../utils/helpers/prioritize-helpers';
import { getEnUSDateTime, getGMTDate } from '../../utils/helpers/date-helper';
import { graphqlClient, TGraphQLSubscriptions } from '../../api/graphql-api-client/graphql-api-client';
import { ListMessagesQuery, MessageState, UpdateBatchMessagesMutation, UpdateMessagesMutation } from '../../graphql/API';
import ErrorBoundaryStore from '../error-boundary-store/error-boundary-store';
import { DEFAULT_CHUNK_SIZE, INCREMENT, INITIAL_VALUE, QUESTION_QUERY_LIMIT, } from '../../constans/prioritize-constants';
import { IQuestionActionDataForm } from '../../components/q-a/questions/question-action-form/question-action-form';
import { v4 as uuidv4 } from 'uuid';
import { localStoragePageInfo } from '../../utils/services/local-storage-service';
import { protectedContentApiClient } from '../../api/api-fetcher/api-client';
import { getErrorBoundaryStore } from '../error-boundary-store/error-boundary-store-instanse';
import PodiumInfoStore from '../podium-info-store/podium-info-store';
import { getPodiumInfoStore } from '../podium-info-store/podium-info-store-instance';
import { ITERATE_NUMBER } from '../../constans/polls-constants';
import { ManageQuestionsNewLabel } from './manage-questions-new-label/manage-questions-new-label';
import { getManageQuestionsNewLabel } from './manage-questions-new-label/manage-questions-new-label-instance';
import { getMixpanelService } from '../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../constans/mixpanel-contants';
import { IErrorObject, IGraphQLErrorResponse } from '../../utils/services/error-parsing-service';

enum SattledPromiseResults {
    REJECTED = 'rejected',
    FULFILLED = 'fulfilled'
}

export type TUpdateQuestion = (id: string | null, state: QuestionState | string, lastChanges: string, prioritize?: number | null) => void;

class QuestionStore {
  public questionList: QuestionEntity[];
  public isDisabled: boolean;
  public isShowCreateQuestion: boolean;
  public activeReviewModeTabOnMobile: QnadaReviewModeTabs;
  public editableQuestionId: string | null;
  private isLocalQuestionBatchAction: boolean;
  private isLocalCreatedQuestions: boolean;
  private isMenuOpen: boolean;
  constructor (
        private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
        private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore(),
        private readonly manageQuestionsNewLabel: ManageQuestionsNewLabel = getManageQuestionsNewLabel()
  ) {
    makeAutoObservable(this);
    this.questionList = [];
    this.isDisabled = false;
    this.isMenuOpen = false;
    this.activeReviewModeTabOnMobile = QnadaReviewModeTabs.FOR_REVIEW;
    this.isShowCreateQuestion = false;
    this.editableQuestionId = null;
    this.isLocalQuestionBatchAction = false;
    this.isLocalCreatedQuestions = false;
  }

  public getManageQuestionNewLabel = (): ManageQuestionsNewLabel => {
    return this.manageQuestionsNewLabel;
  };

  public get isMoreMenuOpen (): boolean {
    return this.isMenuOpen;
  }

  public toggleOpenMenu = (open: boolean): void => {
    this.isMenuOpen = open;
  };

  public getQuestionList = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      const result = await graphqlClient.operation<ListMessagesQuery>({
        query: queries.listMessages,
        variables: {
          presentationId: presentationId,
          limit: QUESTION_QUERY_LIMIT,
        }
      });
        // TODO Need fix graphql response type
          // @ts-ignore
      const { data: { listMessages: { items }}} = result;
      runInAction(() => {
        this.questionList = items.map((item: QuestionEntity) => new QuestionEntity(item));
      });
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public onCreateQuestionSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return graphqlClient.subscriptions({
      query: subscriptions.onCreateMessages,
      variables: {
        presentationId: presentationId,
      },
    }, this.onCreateQuestionCallback, () => this.onCreateQuestionSubscription());
  };

  private onCreateQuestionCallback = (data: { onCreateMessages: IQuestion }): void => {
    const { onCreateMessages: question } = data;
    const newQuestion: QuestionEntity = new QuestionEntity(question);
    this.questionList.unshift(newQuestion);

    if(this.isLocalCreatedQuestions) {
      this.isLocalCreatedQuestions = false;
    } else {
      newQuestion.applyNewLabel();
    }
  };

  private onUpdateQuestionCallback = (data: { onUpdateMessages: IQuestion }): void => {
    const { onUpdateMessages: question } = data;
    this.updateList(new QuestionEntity(question));
  };

  private onBatchUpdateQuestionCallback = (data: { onBatchUpdateMessages: {items: QuestionEntity[]} }):void => {
    const { onBatchUpdateMessages: { items }} = data;
    const entityList = items.map((item: QuestionEntity) => new QuestionEntity(item));

    this.updateBatchList(entityList);
  };


  public onUpdateQuestionSubscription = ():TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return graphqlClient.subscriptions({
      query: subscriptions.onUpdateMessages,
      variables: {
        presentationId: presentationId,
      },
    }, this.onUpdateQuestionCallback, () => this.onUpdateQuestionSubscription());
  };

  public onBatchQuestionsUpdate = ():TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return graphqlClient.subscriptions({
      query: subscriptions.onBatchUpdateMessages,
      variables: {
        presentationId: presentationId,
      },
    }, this.onBatchUpdateQuestionCallback, () => this.onBatchQuestionsUpdate());
  };

  public updateQuestion = async (
    id: string | null,
    state: QuestionState | string,
    lastChanges: string,
    prioritize: number | null = null,
    dataForm?: IQuestionActionDataForm): Promise<void> => {

    if (!id) return;

    const questionEntity = this.getQuestionEntityFromList(id);

    const queryBody = dataForm ? this.compileUpdateQuestionDTO(questionEntity.toDTO(), dataForm) : this.getQueryBody(id, state, prioritize, lastChanges);

    try {
      this.isDisabled = true;
      const response = await graphqlClient.operation<UpdateMessagesMutation>({
        query: mutations.updateMessages,
        variables: {
          input: queryBody
        }
      });
      if(response.data && !dataForm) {
        this.updateQuestionOnUpdateResponse(response.data.updateMessages as IQuestion);
      }
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    } finally {
      this.isDisabled = false;
      if (dataForm) this.setEditableQuestionState(id, EditableState.CLOSED, queryBody);

      let columnType = null;

      if (state === QuestionState.REVIEW) {
        columnType = MIXPANEL_EVENT_TYPES.FOR_REVIEW_COLUMN;
      }
      if (state === QuestionState.PRIORITIZED) {
        columnType = MIXPANEL_EVENT_TYPES.PRIORITIZED_COLUMN;
      }
      if (state === QuestionState.PRESENTER) {
        columnType = MIXPANEL_EVENT_TYPES.PRESENTER_COLUMN;
      }

      getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.QATab, columnType);
    }
  };

  public compileUpdateQuestionDTO = (question: IQuestion, dataForm: IQuestionActionDataForm): IQuestion => {
    return { ...question, ...dataForm };
  };

  private createQuestion = async (inputBody: IQuestion): Promise<void> => {
    try {
      await graphqlClient.operation<UpdateMessagesMutation>({
        query: mutations.createMessages,
        variables: {
          input: inputBody
        }
      });
      this.isLocalCreatedQuestions = true;
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public downloadQuestionCSV = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      const options = { download: true };
      const path = `presenter/${presentationId}/questions/export`;
      await protectedContentApiClient.getFile(path, options);

      getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.QATab, MIXPANEL_EVENT_TYPES.DOWNLOAD);
    } catch (error) {
      this.errorBoundary.setErrorMessage(error as IErrorObject);
    }
  };

  public updateBatchQuestions = async (questions: IQuestion[]): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;
    try {
      this.isDisabled = true;
      await graphqlClient.operation<UpdateBatchMessagesMutation>({
        query: mutations.updateBatchMessages,
        variables: {
          input: questions,
          presentationId: presentationId
        }
      });
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    } finally {
      this.isDisabled = false;
    }
  };

  public updateBatchQuestionsCall = async (currentState: QuestionState, stateToChange: QuestionState): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    const listToSend = this.getList(currentState);

    if (!listToSend.length || !presentationId) {
      return;
    }
    const groupedQuestions: IQuestion[] = prioritizeQuestionsInGroup(listToSend, this.questionList, stateToChange);
    const questionGroupToSend: IQuestion[][] = splitQuestionGroupChunks(groupedQuestions, DEFAULT_CHUNK_SIZE);

    this.updateBatchListsAction(questionGroupToSend);

    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.QATab, MIXPANEL_EVENT_TYPES.SEND_ALL);
  };

  public updateList = (question: QuestionEntity): void => {

    const questionEntity = this.getQuestionEntityFromList(question.messageId);
    const isStateChanged = questionEntity.state !== question.state;

    if (questionEntity.getEditableState() === EditableState.OPENED) {
      questionEntity.setQuestionCopy(question);
      return;
    }

    if (!this.isEqualObjects(questionEntity, question)) {
      if (this.checkSafeInt(question)) {
        this.reIndexList(question);
      }

      questionEntity.updateOnSubscription(question);

      if(isStateChanged) {
        questionEntity.applyHighlight();
      }
    }
  };

  public updateBatchList = (receivedQuestions: QuestionEntity[]): void => {
    this.questionList.forEach((question) => {
      receivedQuestions.some((receivedQuestion) => {
        if (receivedQuestion.messageId === question.messageId) {
          question.setQuestionProperty(receivedQuestion.prioritizeIndex, receivedQuestion.state, receivedQuestion.lastChanges);
          !this.isLocalQuestionBatchAction && question.applyHighlight();
        }
      });
    });

    if(this.isLocalQuestionBatchAction) {
      this.isLocalQuestionBatchAction = false;
    }
  };

  public getList = (type: QuestionState | string): QuestionEntity[] => {
    return this.questionList.filter((q) => q.state === type);
  };

  public updateOrderReviewMode = (question: TQuestionEntity, order: number): void => {
    const lastChanges = getGMTDate();
    const indexQ = this.getQuestionIndex(question.messageId, this.questionList);
    const updatedQuestion: QuestionEntity = new QuestionEntity({ ...question, prioritizeIndex: order });
    this.questionList[indexQ].setQuestionProperty(updatedQuestion.prioritizeIndex, updatedQuestion.state, lastChanges);
    this.updateQuestion(updatedQuestion.messageId, updatedQuestion.state, lastChanges, order);
  };

  public updateMoveReviewModeDnd = (question: TQuestionEntity, state: QuestionState | string, order: number): void => {
    const lastChanges = getGMTDate();
    const qIndex = this.getQuestionIndex(question.messageId, this.questionList);
    const updatedQuestion: QuestionEntity = new QuestionEntity({
      ...question,
      state: state,
      prioritizeIndex: order
    });
    this.questionList[qIndex].setQuestionProperty(updatedQuestion.prioritizeIndex, updatedQuestion.state, lastChanges);
    this.updateQuestion(updatedQuestion.messageId, state, lastChanges, order);

  };

  public getReviewList = (): QuestionEntity[] => {
    return this.questionList.filter((question) => {
      return question.state === QuestionState.REVIEW || question.state === QuestionState.PRESENTER || question.state === QuestionState.PRIORITIZED;
    });
  };

  public getNumberOfQuestionsByListType = (listType: QuestionState): number => {
    return this.questionList.filter((question) => question.state === listType).length;
  };

  public getNumberOfQuestionsByMode = (): number => {
    const currentType = this.podiumInfoStore.isReviewMode ? QuestionState.PRESENTER : QuestionState.REVIEW;
    return this.getNumberOfQuestionsByListType(currentType);
  };

  public setActiveReviewModeTabOnMobile = (tabName: QnadaReviewModeTabs): void => {
    this.activeReviewModeTabOnMobile = tabName;
  };

  public setEditableQuestionState = (questionId: string | null, state: EditableState, edited?: IQueryBody | IQuestion): void => {
    if (!questionId) {
      return;
    }

    const questionEntity = this.getQuestionEntityFromList(questionId);

    if (state === EditableState.CLOSED && !edited) {
      questionEntity.updateEntityOnCopy();
    }

    if (edited) {
      questionEntity.applyChangedFields(edited as IQuestion);
    }

    questionEntity.setIsEditable(state);
  };

  private getQuestionEntityFromList = (questionId: string | null): QuestionEntity => {
    const indexForUpdate = this.getQuestionIndex(questionId, this.questionList);
    return this.questionList[indexForUpdate];
  };

  public setShowCreateQuestion = (value: boolean): void => {
    this.isShowCreateQuestion = value;
  };

  public getEditable = (questionId: string | null): EditableState => {
    if (!questionId) {
      return EditableState.CLOSED;
    }

    const questionEntity = this.getQuestionEntityFromList(questionId);
    return questionEntity.getEditableState();
  };

  public handleCreateQuestion = (dataForm: IQuestionActionDataForm, presentationTitle: string): void => {

    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    const questionToSend: IQuestion = {
      ...dataForm,
      messageId: uuidv4(),
      presentationId: presentationId ?? '',
      sentDate: getEnUSDateTime(),
      state: MessageState.REVIEW,
      lastChanges: getGMTDate(),
      prioritizeIndex: INITIAL_VALUE,
      presentationTitle: presentationTitle,
      senderStatus: SenderStatus.PRESENTER
    };

    const questionEntityDTO = new QuestionEntity(questionToSend);
    this.createQuestion(questionEntityDTO.toDTO());
  };

  private isEqualObjects = (mainObj: TQuestionEntity, secondObj: TQuestionEntity): boolean => {
    return JSON.stringify(mainObj) === JSON.stringify(secondObj);
  };

  private getQuestionIndex = (questionId: string | null, list: TQuestionEntity[]): number => {
    const findIndexCB = (q: IQuestion) => q.messageId === questionId;
    return list.findIndex(findIndexCB);
  };

  public handleSubmitQuestionForm = (
    podiumEmbedId: string,
    question: TQuestionEntity | undefined,
    dataForm: IQuestionActionDataForm,
    presentationTitle: string): void => {
    if (!question) {
      this.handleCreateQuestion(dataForm, presentationTitle);
      this.setShowCreateQuestion(false);
      localStoragePageInfo.setStorageProperty(podiumEmbedId, 'userEmail', dataForm.senderEmail);
      localStoragePageInfo.setStorageProperty(podiumEmbedId, 'userName', dataForm.senderName);
    } else {
      const { messageId, state, lastChanges, prioritizeIndex } = question;
      this.updateQuestion(messageId, state, lastChanges, prioritizeIndex, dataForm);
    }
  };

  private getQueryBody = (questionId: string | null, state: QuestionState | string, prioritize: number | null, lastChanges: string) => {
    if (!prioritize) {
      return {
        messageId: questionId,
        state: state,
        lastChanges: lastChanges,
        prioritizeIndex: getHigherPrioritizeByState({
          list: this.questionList,
          state
        })
      };
    } else {
      return {
        messageId: questionId,
        prioritizeIndex: prioritize,
        state,
        lastChanges: lastChanges
      };
    }
  };

  private checkSafeInt = ({ prioritizeIndex, state, messageId }: TQuestionEntity): boolean => {
    const list: TQuestionEntity[] = state === QuestionState.REVIEW ? this.getReviewList() : this.getList(state);
    const duplicate = checkDuplicateOfPrioritizeIndexInList(list, messageId, prioritizeIndex);
    const maxSafeInt = prioritizeIndex > Number.MAX_SAFE_INTEGER;
    const minSafeInt = prioritizeIndex < 0;
    return duplicate || maxSafeInt || minSafeInt;
  };


    /**This is highly perform re-index function of list part with new Question and it new priority that appears from subscription.
     * It is assumed that list reIndex can be called in case of priorityIndex duplication in same state or in case of
     * huge priorityIndex number that overlap JavaScript Max Number;
     * */

  private reIndexList = (question: QuestionEntity): void => {
    const isNotReviewList: boolean = question.state === QuestionState.REMOVED || question.state === QuestionState.ANSWERED;
    const list: QuestionEntity[] = isNotReviewList ? this.getList(question.state) : this.getReviewList();
    list.splice(this.getQuestionIndex(question.messageId, list), ITERATE_NUMBER, question);
    const listSortedByPriority = sortByPrioritize(list).reverse() as TQuestionEntity[];
    const updatedList = reCalcEachItemPrioritizeIndex(listSortedByPriority, INITIAL_VALUE, INCREMENT);
    const questionGroupToSend: IQuestion[][] = splitQuestionGroupChunks(updatedList, DEFAULT_CHUNK_SIZE);

    this.updateBatchListsAction(questionGroupToSend);
  };

  private updateBatchListsAction = (questionGroupToSend: IQuestion[][]): void => {
    Promise.allSettled(
      questionGroupToSend.map((questionsChunk: IQuestion[]) => this.updateBatchQuestions(questionsChunk)))
      .then(result => result.map((singleResult): void => {
        if (singleResult.status === SattledPromiseResults.REJECTED) {
          this.errorBoundary.setGraphQLErrorMessage(singleResult.reason);
        }
      }))
      .finally(() => {
        this.isLocalQuestionBatchAction = true;
      });
  };

  private updateQuestionOnUpdateResponse (question: IQuestion): void {
    if(!question) return;
    const questionEntity: QuestionEntity = this.getQuestionEntityFromList(question.messageId);
    questionEntity.updateOnResponse(question);
  }
}

export type TQuestionStore = typeof QuestionStore;
export default QuestionStore;

